// lib/roleAccess.ts
// Satu sumber konfigurasi akses per role di frontend. Backend tetap sumber
// kebenaran (403); file ini hanya menyembunyikan menu/tombol yang pasti
// ditolak supaya Sales tidak "nyasar" ke halaman yang tidak bisa dipakai.

export const ROLE = {
  ADMIN: "Admin",
  STAFF: "Staff",
  SALES: "Sales",
} as const;

type KnownRole = (typeof ROLE)[keyof typeof ROLE];

/** Nama role resmi (case-insensitive), atau null kalau kosong/tidak dikenal. */
const normalizeRole = (role?: string | null): KnownRole | null => {
  const value = (role ?? "").trim().toLowerCase();
  return Object.values(ROLE).find((known) => known.toLowerCase() === value) ?? null;
};

export const isKnownRole = (role?: string | null) => normalizeRole(role) !== null;

export const isSalesRole = (role?: string | null) => normalizeRole(role) === ROLE.SALES;

/**
 * role -> prefix path menu yang boleh diakses. "all" = semua menu.
 * Role kosong/tidak dikenal tidak terdaftar di sini -> tidak boleh membuka
 * halaman apa pun (paling terbatas) dan diarahkan ke login.
 */
export const ROLE_MENU_PATHS: Record<KnownRole, string[] | "all"> = {
  [ROLE.ADMIN]: "all",
  [ROLE.STAFF]: "all",
  [ROLE.SALES]: ["/quotation", "/dos"],
};

/**
 * Halaman yang hanya boleh dibuka Sales (form buat/edit DOS). Role lain
 * yang membukanya diarahkan ke daftar DOS (lihat getRedirectPath).
 */
export const SALES_ONLY_PATH_PREFIXES = ["/dos/add", "/dos/edit"];
const SALES_ONLY_REDIRECT = "/dos";

const matchesPrefix = (pathname: string, prefix: string) => {
  const path = pathname.toLowerCase();
  const base = prefix.toLowerCase();
  return path === base || path.startsWith(`${base}/`);
};

export const canAccessPath = (role: string | null | undefined, pathname: string) => {
  const known = normalizeRole(role);
  if (!known) return false;

  if (
    known !== ROLE.SALES &&
    SALES_ONLY_PATH_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix))
  ) {
    return false;
  }

  const allowed = ROLE_MENU_PATHS[known];
  if (allowed === "all") return true;
  return allowed.some((prefix) => matchesPrefix(pathname, prefix));
};

/**
 * Halaman tujuan setelah login / saat membuka "/" atau URL terlarang.
 * Role kosong/tidak dikenal -> /login.
 */
export const getHomePath = (role?: string | null) => {
  const known = normalizeRole(role);
  if (known === ROLE.SALES) return "/quotation";
  if (known) return "/dashboard";
  return "/login";
};

/**
 * Tujuan pengalihan saat role tidak boleh membuka `pathname`:
 * - Admin/Staff di form buat/edit DOS -> /dos.
 * - Selain itu -> halaman utama role (getHomePath).
 */
export const getRedirectPath = (role: string | null | undefined, pathname: string) => {
  const known = normalizeRole(role);
  if (
    known &&
    known !== ROLE.SALES &&
    SALES_ONLY_PATH_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix))
  ) {
    return SALES_ONLY_REDIRECT;
  }
  return getHomePath(role);
};

/**
 * CERMIN dari backend: src/quotation/quotationEditLock.ts
 * (SALES_EDIT_LOCKED_CODES). Backend adalah sumber kebenaran: ia yang menolak
 * edit oleh Sales dengan 403. Konstanta ini hanya untuk menonaktifkan tombol
 * Edit di UI. Kalau daftar di backend berubah, ubah juga di sini.
 */
export const SALES_EDIT_LOCKED_CODES = ["DO_CREATED", "PO_CREATED", "WO_CREATED"];

export const isSalesEditLocked = (code?: string | null) =>
  Boolean(code) && SALES_EDIT_LOCKED_CODES.includes(code as string);
